import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { BookingPayment } from '@limo/database'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { CreditCard, Plus, ExternalLink, Undo2, Link2 } from 'lucide-react'

interface BookingPaymentsSectionProps {
  bookingId: string
  totalAmount: number
  adjustmentsTotal?: number
  onChargeClick?: () => void
  onRefundClick?: () => void
  /** Opens the pay-balance link dialog. Omitted when nothing is owed. */
  onPaymentLinkClick?: () => void
  /** For backwards compatibility: include original payment from booking if not in booking_payments */
  originalPaymentAmount?: number
  originalPaymentStatus?: string
  originalPaymentIntentId?: string
  /**
   * Extra booking_payments rows to fold into the totals. Used for paired
   * round trips so the sibling leg's payments count towards the combined
   * Paid figure. Each entry is displayed in the history list alongside the
   * row's own payments; refunds and duplicates are handled by the dedupe
   * below. Defaults to an empty array.
   */
  additionalPayments?: BookingPayment[]
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount)

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

const paymentStatusStyles: Record<
  string,
  { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }
> = {
  pending: { variant: 'secondary', label: 'Pending' },
  succeeded: { variant: 'default', label: 'Succeeded' },
  failed: { variant: 'destructive', label: 'Failed' },
  refunded: { variant: 'outline', label: 'Refunded' },
}

export function BookingPaymentsSection({
  bookingId,
  totalAmount,
  adjustmentsTotal = 0,
  onChargeClick,
  onRefundClick,
  onPaymentLinkClick,
  originalPaymentAmount,
  originalPaymentStatus,
  originalPaymentIntentId,
  additionalPayments = [],
}: BookingPaymentsSectionProps) {
  const { data: payments, isLoading } = useQuery({
    queryKey: ['booking-payments', bookingId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('booking_payments')
        .select('*')
        .eq('booking_id', bookingId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data as BookingPayment[]
    },
    enabled: !!bookingId,
  })

  // Check if the original payment is already recorded in booking_payments
  // Only consider succeeded payments - refunds share the same payment_intent_id but aren't the original payment
  const originalPaymentRecorded = payments?.some(
    (p) => p.stripe_payment_intent_id === originalPaymentIntentId && p.status === 'succeeded'
  )

  // For backwards compatibility: if original payment exists but isn't in booking_payments yet,
  // create a synthetic payment record to display
  const hasUnrecordedOriginalPayment =
    originalPaymentStatus === 'paid' &&
    originalPaymentAmount &&
    originalPaymentAmount > 0 &&
    !originalPaymentRecorded

  // Extended payment type to include stripe_refund_id
  type ExtendedPayment = BookingPayment & { stripe_refund_id?: string | null }

  // Combine recorded payments with the synthetic original payment (if any)
  // and with any sibling-leg payments the parent passed in (`additionalPayments`).
  //
  // Dedupe by id: paired-leg Stripe webhook behavior (new) writes one
  // booking_payments row per leg with distinct ids, so merging is safe.
  // Legacy paired rows (one combined payment recorded on a single leg) also
  // stay unique because only ONE leg has the row; the sibling fetch yields
  // an empty array. We also dedupe by `stripe_payment_intent_id + status`
  // as a belt-and-suspenders guard against the same charge appearing twice
  // after any future manual backfill.
  const mergedRecorded = [...(payments || []), ...additionalPayments]
  const seenIds = new Set<string>()
  const seenPIStatus = new Set<string>()
  const dedupedRecorded = mergedRecorded.filter((p) => {
    if (seenIds.has(p.id)) return false
    seenIds.add(p.id)
    const key = `${p.stripe_payment_intent_id ?? ''}::${p.status}`
    if (key !== '::' && seenPIStatus.has(key)) return false
    seenPIStatus.add(key)
    return true
  })

  const allPayments: (ExtendedPayment | {
    id: string
    amount: number
    status: string
    stripe_payment_intent_id: string | null
    stripe_refund_id?: string | null
    description: string
    created_at: string
    isSynthetic?: boolean
  })[] = [
    ...dedupedRecorded,
    ...(hasUnrecordedOriginalPayment
      ? [
          {
            id: 'original-payment',
            amount: originalPaymentAmount,
            status: 'succeeded',
            stripe_payment_intent_id: originalPaymentIntentId || null,
            description: 'Initial booking payment',
            created_at: new Date().toISOString(), // We don't have the exact date
            isSynthetic: true,
          },
        ]
      : []),
  ]

  // Calculate totals including the synthetic payment
  const succeededPayments = allPayments.filter((p) => p.status === 'succeeded')
  const refundedPayments = allPayments.filter((p) => p.status === 'refunded')

  const totalPaid = succeededPayments.reduce(
    (sum, p) => sum + Number(p.amount),
    0
  )
  // Refunds are stored as negative amounts
  const totalRefunded = refundedPayments.reduce(
    (sum, p) => sum + Math.abs(Number(p.amount)),
    0
  )
  const adjustedTotal = totalAmount + adjustmentsTotal
  const balanceDue = adjustedTotal - totalPaid + totalRefunded

  // Can refund if there are succeeded payments and some amount is refundable
  const canRefund = onRefundClick && totalPaid > totalRefunded

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Payments
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Payments
        </CardTitle>
        <div className="flex gap-2">
          {canRefund && (
            <Button variant="outline" size="sm" onClick={onRefundClick}>
              <Undo2 className="h-3 w-3 mr-1" />
              Issue Refund
            </Button>
          )}
          {/* Two ways to collect a balance, deliberately side by side: charge
              the card already on file, or send the customer a link to pay it
              themselves. The link is the one that works when the saved card has
              expired or someone else is paying. */}
          {onPaymentLinkClick && balanceDue > 0 && (
            <Button variant="outline" size="sm" onClick={onPaymentLinkClick}>
              <Link2 className="h-3 w-3 mr-1" />
              Payment Link
            </Button>
          )}
          {onChargeClick && balanceDue > 0 && (
            <Button variant="outline" size="sm" onClick={onChargeClick}>
              <Plus className="h-3 w-3 mr-1" />
              Charge Customer
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Payment summary */}
          <div className={`grid ${totalRefunded > 0 ? 'grid-cols-4' : 'grid-cols-3'} gap-4 p-3 bg-muted rounded-lg`}>
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="font-medium">{formatCurrency(adjustedTotal)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Paid</p>
              <p className="font-medium text-green-600">
                {formatCurrency(totalPaid)}
              </p>
            </div>
            {totalRefunded > 0 && (
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Refunded</p>
                <p className="font-medium text-orange-600">
                  -{formatCurrency(totalRefunded)}
                </p>
              </div>
            )}
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Balance</p>
              <p
                className={`font-medium ${
                  balanceDue > 0
                    ? 'text-red-600'
                    : balanceDue < 0
                    ? 'text-green-600'
                    : ''
                }`}
              >
                {formatCurrency(Math.abs(balanceDue))}
                {balanceDue < 0 && ' (Credit)'}
              </p>
            </div>
          </div>

          {/* Payment list */}
          {allPayments.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Payment History
              </p>
              {allPayments.map((payment) => {
                const isRefund = payment.status === 'refunded'
                const stripeRefundId = 'stripe_refund_id' in payment ? payment.stripe_refund_id : null

                return (
                  <div
                    key={payment.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${isRefund ? 'text-orange-600' : ''}`}>
                          {isRefund ? '-' : ''}
                          {formatCurrency(Math.abs(Number(payment.amount)))}
                        </span>
                        <Badge
                          variant={
                            paymentStatusStyles[payment.status]?.variant || 'secondary'
                          }
                          className="text-xs"
                        >
                          {paymentStatusStyles[payment.status]?.label || payment.status}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatDate(payment.created_at)}
                        {payment.description && ` - ${payment.description}`}
                      </div>
                    </div>
                    {/* Show link to Stripe - for refunds link to refund, otherwise to payment */}
                    {(stripeRefundId || payment.stripe_payment_intent_id) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8"
                        onClick={() => {
                          // Open Stripe dashboard
                          const stripeUrl = stripeRefundId
                            ? `https://dashboard.stripe.com/refunds/${stripeRefundId}`
                            : `https://dashboard.stripe.com/payments/${payment.stripe_payment_intent_id}`
                          window.open(stripeUrl, '_blank')
                        }}
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        <span className="text-xs font-mono">
                          {(stripeRefundId || payment.stripe_payment_intent_id)?.slice(-8)}
                        </span>
                      </Button>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No payments recorded yet
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
